import moment from 'moment';
import qs from 'qs';
import * as React from 'react';
import { withRouter } from 'react-router-dom';

import ReportHeader from './reportHeader';
import { RecommendedGlyphTooltip, TROUBLE_PROCESSING_MESSAGE, accountIcon, baseImageSrc, noDataYet, } from './shared';

import { requestGet, requestPost, requestPut, } from '../../../../../../modules/request/index';
import useSnackbarMonitor from '../../../../../Shared/hooks/useSnackbarMonitor';
import {
  Checkbox,
  EXTRA_SMALL_ICON_BASE_SRC,
  Snackbar,
  Spinner,
  Tooltip,
  defaultSnackbarTimeout,
  helpIcon,
} from '../../../../../Shared/index';
import DemoGuardedButton from '../../../shared/demoGuardedButton';

const READY_FOR_GROWTH = 'Ready'
const ELIGIBLE_FOR_GROWTH = 'Eligible'
const NOT_YET_READY = 'Not Yet Ready'

const FULLY_PRACTICED = 'Fully Practiced'
const PARTIALLY_PRACTICED = 'Partially Practiced'
const NOT_YET_PRACTICED = 'Not Practiced'

const DASH = '—'

const sortIcon = <img alt="" src={`${EXTRA_SMALL_ICON_BASE_SRC}/arrow-down.svg`} />
const checkCircleIcon = <img alt="" src={`${EXTRA_SMALL_ICON_BASE_SRC}/check-circle.svg`} />
const clockCheckIcon = <img alt="" src={`${EXTRA_SMALL_ICON_BASE_SRC}/clock-check.svg`} />
const toggleArrowClosedIcon = <img alt="" src={`${baseImageSrc}/icons/data_table/toggle-arrow-closed.svg`} />
const toggleArrowExpandedIcon = <img alt="" src={`${baseImageSrc}/icons/data_table/toggle-arrow-expanded.svg`} />

const HEADERS = [
  { label: 'Student Name', tooltipText: 'Students who have been assigned the baseline diagnostic in this class.', sortable: true },
  { label: 'Practiced Skills', tooltipText: 'The number of skills (i.e., practice recommendation activity packs) the student has been assigned and has fully or partially practiced.' },
  { label: 'Not Practiced Skills', tooltipText: 'The number of skills (i.e., practice recommendation activity packs) the student has been assigned, but has not yet practiced.' },
  { label: 'Not Assigned Skills', tooltipText: 'The number of skills (i.e., practice recommendation activity packs) the student has not been assigned.' },
  { label: 'Activities Completed', tooltipText: 'The number of activities in the practice recommendation packs that the student has completed out of all the activities in the practice recommendation activity packs the student has been assigned.' },
  { label: 'Activities Average Score', tooltipText: 'The average of the scores the student has earned on the activities in the practice recommendation activity packs they have completed.' },
  { label: 'Growth Assignment Recommendation', tooltipText: 'Quill’s recommendation of whether the student is ready, is eligible, or is not ready to be assigned the growth diagnostic, depends on the student’s progress through the activities in their assigned practice recommendation activity packs.<br/><br/>“Ready” means that the student has completed all the activities in all the practice recommendation activity packs they have been assigned.<br/><br/>“Eligible” means that the student has completed all the activities in at least one, but not all, practice recommendation activity pack they have been assigned.<br/><br/>“Not Yet Ready” means that the student has not yet completed all the activities in any of the practice recommendation activity packs they have been assigned.' },
  { label: 'Assign Growth Diagnostic', tooltipText: 'Select students and assign the growth diagnostic to measure their progress after practice.' }
]

interface StudentSkill {
  activity_pack_id: number;
  recommended: boolean;
  assigned: boolean;
  activities_completed: number | null;
  average_score: number | null;
}

interface OverviewStudent {
  id: number;
  name: string;
  completed: boolean;
  skills: StudentSkill[];
  growth_assignment: { assigned: boolean, unit_id: number, published: boolean, publish_date: string, early_released_at: string | null } | null;
}

interface Skill {
  activity_pack_id: number;
  name: string;
  activity_count: number;
}

function practiceStatus(studentSkill: StudentSkill, activityCount: number) {
  if (!studentSkill.assigned) { return null }
  if (activityCount > 0 && studentSkill.activities_completed >= activityCount) { return FULLY_PRACTICED }
  if (studentSkill.activities_completed > 0) { return PARTIALLY_PRACTICED }
  return NOT_YET_PRACTICED
}

function deriveStudentStats(student: OverviewStudent, activityCountByPackId: { [packId: number]: number }) {
  const assignedSkills = student.skills.filter(skill => skill.assigned)
  const packTotal = (skill: StudentSkill) => activityCountByPackId[skill.activity_pack_id] || 0

  const statuses = assignedSkills.map(skill => practiceStatus(skill, packTotal(skill)))
  const fullyPracticed = statuses.filter(status => status === FULLY_PRACTICED).length
  const practicedSkills = statuses.filter(status => [FULLY_PRACTICED, PARTIALLY_PRACTICED].includes(status)).length
  const notPracticedSkills = statuses.filter(status => status === NOT_YET_PRACTICED).length
  const notAssignedSkills = student.skills.length - assignedSkills.length
  const activitiesTotal = assignedSkills.reduce((sum, skill) => sum + packTotal(skill), 0)
  const activitiesCompleted = assignedSkills.reduce((sum, skill) => sum + skill.activities_completed, 0)
  const scoredSkills = assignedSkills.filter(skill => skill.average_score !== null && skill.activities_completed > 0)
  const scoreWeight = scoredSkills.reduce((sum, skill) => sum + skill.activities_completed, 0)
  const averageScore = scoreWeight ? Math.round(scoredSkills.reduce((sum, skill) => sum + (skill.average_score * skill.activities_completed), 0) / scoreWeight) : null

  let recommendationLevel = NOT_YET_READY
  if (assignedSkills.length && fullyPracticed === assignedSkills.length) {
    recommendationLevel = READY_FOR_GROWTH
  } else if (fullyPracticed > 0) {
    recommendationLevel = ELIGIBLE_FOR_GROWTH
  }

  return { practicedSkills, notPracticedSkills, notAssignedSkills, activitiesTotal, activitiesCompleted, averageScore, recommendationLevel, }
}

const HeaderCell = ({ label, tooltipText, sortable, sortDirection, toggleSortDirection, }) => (
  <th aria-sort={sortable ? (sortDirection === 'asc' ? 'ascending' : 'descending') : undefined} scope="col">
    <div className="header-cell-content">
      <span className="header-label">{label}</span>
      {sortable && (
        <button aria-label="Sort by student name" className={`interactive-wrapper focus-on-light sort-button ${sortDirection === 'desc' ? 'is-descending' : ''}`} onClick={toggleSortDirection} type="button">
          {sortIcon}
        </button>
      )}
      {tooltipText && (
        <Tooltip
          fixedPosition={true}
          isTabbable={false}
          tooltipText={tooltipText}
          tooltipTriggerText={<button aria-label={`About ${label}`} className="interactive-wrapper focus-on-light" type="button"><img alt="" src={helpIcon.src} /></button>}
        />
      )}
    </div>
  </th>
)

const RecommendationTag = ({ recommendationLevel, }) => {
  if (recommendationLevel === READY_FOR_GROWTH) {
    return <span className="chip recommendation-tag">{checkCircleIcon}<span>{READY_FOR_GROWTH}</span></span>
  }

  return <span className="chip recommendation-tag">{recommendationLevel}</span>
}

const GrowthAssignmentCell = ({ classroomId, student, recommendationLevel, isSelected, toggleStudentSelection, }) => {
  const { growth_assignment, name, } = student

  function handleCheckboxClick() { toggleStudentSelection(student.id) }

  if (growth_assignment) {
    const icon = growth_assignment.published ? checkCircleIcon : clockCheckIcon
    const label = growth_assignment.published ? 'Published' : 'Scheduled'
    const formattedDate = moment(growth_assignment.early_released_at || growth_assignment.publish_date).format('M/D h:mm A')
    return (
      <a
        aria-label={`${label} ${formattedDate}. View the growth diagnostic pack for ${name} in Manage Activities.`}
        className="growth-assignment-status focus-on-light"
        href={`/teachers/classrooms/activity_planner?classroom_id=${classroomId}#${growth_assignment.unit_id}Name`}
      >
        {icon}
        <div>
          <span className="status-label">{label}</span>
          <span>{formattedDate}</span>
        </div>
      </a>
    )
  }

  const isRecommended = recommendationLevel === READY_FOR_GROWTH
  const ariaLabel = isSelected ? `Unselect ${name} for growth diagnostic assignment` : `Select ${name} for growth diagnostic assignment`

  return (
    <button aria-label={ariaLabel} className={`interactive-wrapper focus-on-light growth-assignment-checkbox ${isRecommended ? 'recommended' : ''}`} onClick={handleCheckboxClick} type="button">
      <Checkbox selected={isSelected} />
      {isRecommended ? <RecommendedGlyphTooltip /> : null}
    </button>
  )
}

const SkillDetailRow = ({ skill, studentSkill, }) => {
  const { assigned, activities_completed, average_score, } = studentSkill
  const total = skill.activity_count
  const status = practiceStatus(studentSkill, total)

  let statusTag = <span className="practice-tag not-assigned">Not Assigned</span>
  if (status === FULLY_PRACTICED) {
    statusTag = <span className="practice-tag fully-practiced">{checkCircleIcon}<span>{FULLY_PRACTICED}</span></span>
  } else if (status === PARTIALLY_PRACTICED) {
    statusTag = <span className="practice-tag partially-practiced">{checkCircleIcon}<span>{PARTIALLY_PRACTICED}</span></span>
  } else if (status === NOT_YET_PRACTICED) {
    statusTag = <span className="practice-tag not-practiced">{NOT_YET_PRACTICED}</span>
  }

  return (
    <tr>
      <th scope="row"><span className="skill-name-tag">{skill.name}</span></th>
      <td>{statusTag}</td>
      <td>{assigned ? `${activities_completed} of ${total}` : DASH}</td>
      <td>{assigned && average_score !== null ? `${average_score}%` : DASH}</td>
    </tr>
  )
}

const SkillDetail = ({ skills, student, }) => {
  const studentSkillsByPackId = Object.fromEntries(student.skills.map(studentSkill => [studentSkill.activity_pack_id, studentSkill]))

  return (
    <div className="skill-detail">
      <table>
        <caption className="sr-only">Skill details for {student.name}</caption>
        <thead>
          <tr>
            <th scope="col">Diagnostic Skills</th>
            <th scope="col">Practice Status</th>
            <th scope="col">Activities Completed</th>
            <th scope="col">Activities Average Score</th>
          </tr>
        </thead>
        <tbody>
          {skills.map(skill => <SkillDetailRow key={skill.activity_pack_id} skill={skill} studentSkill={studentSkillsByPackId[skill.activity_pack_id]} />)}
        </tbody>
      </table>
    </div>
  )
}

const StudentRow = ({ classroomId, student, skills, activityCountByPackId, isExpanded, toggleStudentExpansion, isSelected, toggleStudentSelection, }) => {
  const { id, name, completed, } = student
  const stats = deriveStudentStats(student, activityCountByPackId)

  function handleExpandClick() { toggleStudentExpansion(id) }

  return (
    <React.Fragment>
      <tr>
        <td className="name-cell">
          <button aria-controls={`student-skill-detail-${id}`} aria-expanded={isExpanded} aria-label={`Skill details for ${name}`} className="interactive-wrapper focus-on-light chip-button" onClick={handleExpandClick} type="button">
            {isExpanded ? toggleArrowExpandedIcon : toggleArrowClosedIcon}
            <span className="chip">{accountIcon}<span>{name}</span></span>
          </button>
          {completed ? null : <span className="name-section-subheader">Diagnostic not completed</span>}
        </td>
        <td>{completed ? stats.practicedSkills : DASH}</td>
        <td>{completed ? stats.notPracticedSkills : DASH}</td>
        <td>{completed ? stats.notAssignedSkills : DASH}</td>
        <td>{stats.activitiesTotal ? `${stats.activitiesCompleted} of ${stats.activitiesTotal}` : DASH}</td>
        <td>{stats.averageScore === null ? DASH : `${stats.averageScore}%`}</td>
        <td>{completed ? <RecommendationTag recommendationLevel={stats.recommendationLevel} /> : DASH}</td>
        <td>
          <GrowthAssignmentCell
            classroomId={classroomId}
            isSelected={isSelected}
            recommendationLevel={completed ? stats.recommendationLevel : null}
            student={student}
            toggleStudentSelection={toggleStudentSelection}
          />
        </td>
      </tr>
      {isExpanded && (
        <tr className="skill-detail-row" id={`student-skill-detail-${id}`}>
          <td colSpan={HEADERS.length}>
            <SkillDetail skills={skills} student={student} />
          </td>
        </tr>
      )}
    </React.Fragment>
  )
}

export const AssignGrowthDiagnostic = ({ location, match, mobileNavigation, postDiagnosticActivityId, postDiagnosticUnitTemplateId, refreshReadyStudentCount, }) => {
  const [loading, setLoading] = React.useState(true)
  const [skills, setSkills] = React.useState<Skill[]>([])
  const [students, setStudents] = React.useState<OverviewStudent[]>([])
  const [selectedStudentIds, setSelectedStudentIds] = React.useState<number[]>([])
  const [expandedStudentIds, setExpandedStudentIds] = React.useState<number[]>([])
  const [assigning, setAssigning] = React.useState(false)
  const [showSnackbar, setShowSnackbar] = React.useState(false)
  const [sortDirection, setSortDirection] = React.useState('asc')
  const [earlyPublish, setEarlyPublish] = React.useState(false)
  // bumped on every checkbox interaction so a stale overview response cannot
  // clobber newer optimistic state
  const earlyPublishInteractionCountRef = React.useRef(0)

  useSnackbarMonitor(showSnackbar, setShowSnackbar, defaultSnackbarTimeout)

  const { activityId, classroomId, } = match.params
  const unitId = qs.parse(location.search.replace('?', '')).unit
  const unitQueryString = unitId ? `?unit_id=${unitId}` : ''

  React.useEffect(() => {
    setLoading(true)
    setSelectedStudentIds([])
    setExpandedStudentIds([])
    getOverview()
  }, [activityId, classroomId, unitId])

  function getOverview() {
    const interactionCountAtRequest = earlyPublishInteractionCountRef.current
    requestGet(`/teachers/progress_reports/growth_diagnostic_assignment_overview/${classroomId}/activity/${activityId}${unitQueryString}`, (data) => {
      setSkills(data.skills)
      setStudents(data.students)
      if (earlyPublishInteractionCountRef.current === interactionCountAtRequest) {
        setEarlyPublish(Boolean(data.early_release_on_practice_completion))
      }
      setLoading(false)
    })
  }

  const activityCountByPackId = Object.fromEntries(skills.map(skill => [skill.activity_pack_id, skill.activity_count]))
  const unassignedStudents = students.filter(student => !student.growth_assignment)
  const sortedStudents = sortDirection === 'asc' ? students : [...students].reverse()

  function toggleStudentSelection(studentId) {
    setSelectedStudentIds(selected => selected.includes(studentId) ? selected.filter(id => id !== studentId) : selected.concat([studentId]))
  }

  function toggleStudentExpansion(studentId) {
    setExpandedStudentIds(expanded => expanded.includes(studentId) ? expanded.filter(id => id !== studentId) : expanded.concat([studentId]))
  }

  function handleSelectAllClick() {
    setSelectedStudentIds(unassignedStudents.map(student => student.id))
  }

  function handleSelectAllRecommendedClick() {
    const recommendedIds = unassignedStudents.filter(student => {
      if (!student.completed) { return false }

      return [READY_FOR_GROWTH, ELIGIBLE_FOR_GROWTH].includes(deriveStudentStats(student, activityCountByPackId).recommendationLevel)
    }).map(student => student.id)
    setSelectedStudentIds(recommendedIds)
  }

  function handleDeselectAllClick() {
    setSelectedStudentIds([])
  }

  function toggleSortDirection() {
    setSortDirection(direction => direction === 'asc' ? 'desc' : 'asc')
  }

  function toggleEarlyPublish() {
    earlyPublishInteractionCountRef.current += 1
    const newValue = !earlyPublish
    setEarlyPublish(newValue)
    requestPut('/teachers/units/set_growth_diagnostic_early_release', {
      activity_id: postDiagnosticActivityId,
      classroom_id: Number(classroomId),
      early_release_on_practice_completion: newValue,
    }, () => {}, () => {
      earlyPublishInteractionCountRef.current += 1
      setEarlyPublish(!newValue)
      alert(TROUBLE_PROCESSING_MESSAGE)
    })
  }

  // the early publish setting is sent only when checked, so newly created
  // packs pick it up; an unchecked box sends nothing rather than false, so a
  // stale tab cannot revert a setting saved elsewhere
  function handleAssignClick() {
    setAssigning(true)
    requestPost('/teachers/units/assign_growth_diagnostic', {
      activity_id: postDiagnosticActivityId,
      unit_template_id: postDiagnosticUnitTemplateId,
      ...(earlyPublish ? { early_release_on_practice_completion: true } : {}),
      classrooms: [{ id: Number(classroomId), student_ids: selectedStudentIds, }]
    }, () => {
      setAssigning(false)
      setSelectedStudentIds([])
      setShowSnackbar(true)
      getOverview()
      refreshReadyStudentCount?.()
    }, () => {
      alert(TROUBLE_PROCESSING_MESSAGE)
      setAssigning(false)
    })
  }

  if (loading) { return <Spinner /> }

  let assignButton = <button className="quill-button contained green small disabled focus-on-light" type="button">Assign</button>
  if (assigning) {
    assignButton = <button className="quill-button contained green small disabled focus-on-light" type="button">Assigning...</button>
  } else if (selectedStudentIds.length) {
    assignButton = <DemoGuardedButton className="quill-button contained green small focus-on-light" onClick={handleAssignClick} type="button">Assign</DemoGuardedButton>
  }

  return (
    <main className="assign-growth-diagnostic-container">
      <Snackbar text="Growth diagnostic assigned" visible={showSnackbar} />
      <ReportHeader
        pageName="Assign Growth Diagnostic"
        subHeader="Assign growth diagnostic to students"
        tooltipText="Select the students you would like to assign the growth diagnostic to. Students who have fully practiced all their recommended skills are ready to show growth."
      />
      {mobileNavigation}
      {students.length ? (
        <React.Fragment>
          <div className="assign-growth-diagnostic-buttons">
            <div className="selection-buttons">
              <button className="quill-button small outlined grey focus-on-light" onClick={handleSelectAllClick} type="button">Select all</button>
              <button className="quill-button small outlined grey focus-on-light" onClick={handleSelectAllRecommendedClick} type="button">Select all recommended</button>
              <button className="quill-button small outlined grey focus-on-light" onClick={handleDeselectAllClick} type="button">Deselect all</button>
            </div>
            <div className="assign-controls">
              <Checkbox
                label="If I have already scheduled the growth diagnostic, publish it immediately when skills are fully practiced."
                onClick={toggleEarlyPublish}
                selected={earlyPublish}
              />
              {assignButton}
            </div>
          </div>
          <div className="assign-growth-diagnostic-table-wrapper">
            <table aria-label="Assign growth diagnostic to students" className="assign-growth-diagnostic-table">
              <thead>
                <tr>
                  {HEADERS.map(header => (
                    <HeaderCell
                      key={header.label}
                      label={header.label}
                      sortable={header.sortable}
                      sortDirection={sortDirection}
                      toggleSortDirection={toggleSortDirection}
                      tooltipText={header.tooltipText}
                    />
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortedStudents.map(student => (
                  <StudentRow
                    activityCountByPackId={activityCountByPackId}
                    classroomId={classroomId}
                    isExpanded={expandedStudentIds.includes(student.id)}
                    isSelected={selectedStudentIds.includes(student.id)}
                    key={student.id}
                    skills={skills}
                    student={student}
                    toggleStudentExpansion={toggleStudentExpansion}
                    toggleStudentSelection={toggleStudentSelection}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </React.Fragment>
      ) : noDataYet}
    </main>
  )
}

export default withRouter(AssignGrowthDiagnostic)
